import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", message: "" };

export const ContactMessageForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm(initial);
      toast.success("Message received — a broker will reply within 24 hours (Mon–Fri).");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call 888.275.0098.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/5 p-8"
        data-testid="contact-message-success"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] flex-shrink-0">
            <MessageSquare size={16} />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
              Message received
            </div>
            <p className="mt-3 text-[color:var(--bone)]/80 leading-relaxed">
              A broker will reply to <span className="text-[color:var(--bone)]">{form.email || "your inbox"}</span> within 24 hours during business hours (Monday–Friday).
              You'll also get a confirmation email — check spam if you don't see it in a minute.
            </p>
            <button
              type="button"
              onClick={() => setDone(false)}
              className="mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--gold)] hover:underline"
              data-testid="contact-message-send-another"
            >
              Send another message →
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
      data-testid="contact-message-form"
    >
      <div className="overline">Send a message</div>
      <p className="text-[color:var(--bone)]/70 leading-relaxed">
        Have a quick question about products, timelines, or anything else? Drop a note below and a
        broker will reply within 24 hours during business hours (Mon–Fri). For a full funding request, use{" "}
        <Link to="/apply" className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40">
          the application
        </Link>{" "}
        instead.
      </p>

      <div>
        <label className="block font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--bone)]/55 mb-2">
          Your name
        </label>
        <input
          type="text"
          data-testid="contact-message-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="editorial-input w-full"
          placeholder="First and last"
        />
      </div>

      <div>
        <label className="block font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--bone)]/55 mb-2">
          Email
        </label>
        <input
          type="email"
          data-testid="contact-message-email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="editorial-input w-full"
          placeholder="you@business.com"
        />
      </div>

      <div>
        <label className="block font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--bone)]/55 mb-2">
          Message
        </label>
        <textarea
          data-testid="contact-message-body"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          rows={5}
          className="editorial-input w-full resize-none"
          placeholder="What can we help with?"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-message-submit"
        className="inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-8 py-4 font-mono text-xs tracking-[0.24em] uppercase disabled:opacity-60 hover:bg-[color:var(--gold-strong)] transition-colors"
      >
        {loading ? "Sending…" : "Send Message"}
        <ArrowRight size={14} />
      </button>

      <p
        data-testid="contact-message-consent"
        className="text-xs text-[color:var(--bone)]/55 leading-relaxed max-w-xl"
      >
        By sending, you agree to our{" "}
        <Link to="/terms" className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)]">
          Terms
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)]">
          Privacy Policy
        </Link>
        . We use your info only to reply to you.
      </p>
    </motion.form>
  );
};

export default ContactMessageForm;
